import { useCan } from '@features/auth';
import { absoluteTime, relativeTime } from '@lib/time';
import {
    Badge,
    Button,
    Card,
    CardHeader,
    EmptyState,
    ErrorState,
    IconArrowLeft,
    IconPlus,
    LoadingRows,
    PropagationNotice,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useState } from 'react';
import { Link } from 'react-router';

import { defaultWriteFrom } from '../api/default-write';
import { useClearOverride, useFlag, useSetDefault, useSetOverride } from '../hooks/use-flags';
import { LIFECYCLE_LABEL, LIFECYCLE_TONE } from '../schemas/lifecycle';

import { EnvironmentMatrix } from './EnvironmentMatrix';
import { FlagHistory } from './FlagHistory';
import { OverrideDialog } from './OverrideDialog';
import { type PlatformDefaultChange } from './platform-default-confirm-copy';
import { PlatformDefaultConfirmDialog } from './PlatformDefaultConfirmDialog';
import { ReadOnlyNotice } from './ReadOnlyNotice';

import type { FlagDetail, FlagOverrideView, FlagVerdict } from '../schemas/flag';

export function FlagDetailPage({ flagKey }: { flagKey: string }) {
    const flag = useFlag(flagKey);

    return (
        <>
            <Link
                to="/flags"
                className="mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-500 hover:text-brand-600"
            >
                <IconArrowLeft className="size-4" /> Feature Flags
            </Link>

            {flag.isPending ? (
                <Card>
                    <LoadingRows label="Loading flag…" />
                </Card>
            ) : flag.isError ? (
                <Card>
                    <ErrorState error={flag.error} onRetry={() => void flag.refetch()} />
                </Card>
            ) : (
                <Loaded flagKey={flagKey} detail={flag.data} />
            )}
        </>
    );
}

function Loaded({ flagKey, detail }: { flagKey: string; detail: FlagDetail }) {
    const { definition, default: current, environmentDefaults, overrides, history } = detail;
    const setDefault = useSetDefault(flagKey);
    const [overriding, setOverriding] = useState(false);
    // NOXSCRUM-1529: a platform-default write (either toggle below shares the
    // same `PUT /default` route and blast radius) is held here, unwritten,
    // until the operator confirms in `PlatformDefaultConfirmDialog`.
    const [pendingDefaultChange, setPendingDefaultChange] = useState<PlatformDefaultChange | null>(
        null,
    );
    // Mirrors the two tiers on the API: one museum is the Ops-CSM job, the
    // platform default is engineer+ because it moves every museum at once.
    const canOverride = useCan('ops-csm');
    const canSetDefault = useCan('engineer');

    return (
        <>
            <ReadOnlyNotice />

            <div className="mb-6 flex items-start gap-4">
                <div className="min-w-0">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-brand-600">
                        Feature Flag
                    </div>
                    <h1 className="break-all font-mono text-[22px] font-semibold tracking-tight text-gray-900">
                        {definition.key}
                    </h1>
                    <p className="mt-1 max-w-2xl text-[13.5px] text-gray-500">
                        {definition.description}
                    </p>
                </div>
                <div className="ml-auto flex shrink-0 gap-2.5">
                    <Button
                        variant="ghost"
                        disabled={!canOverride}
                        onClick={() => {
                            setOverriding(true);
                        }}
                    >
                        <IconPlus className="size-4" />
                        Override a museum
                    </Button>
                </div>
            </div>

            {current.archived && (
                <div
                    role="status"
                    className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900"
                >
                    <strong className="font-semibold">This flag is archived.</strong> It resolves{' '}
                    <code className="font-mono">false</code> everywhere — including for the museums
                    listed below that still carry an override. Un-archive it before any of those
                    values mean anything.
                </div>
            )}

            <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                {/* `min-w-0` is load-bearing: a grid track defaults to
                    `min-width: auto`, so the override table below would size the
                    column to its own content and shove the 320px sidecar off the
                    right edge of the page. The table has its own horizontal
                    scroll; the page must not. */}
                <div className="flex min-w-0 flex-col gap-5">
                    {/* ---------------------------------------- platform default - */}
                    <Card>
                        <CardHeader
                            title="Platform default"
                            subtitle="Every museum without an override"
                        />
                        <div className="flex flex-col gap-3.5 px-5 py-4">
                            <div className="flex items-center gap-3">
                                <Toggle
                                    on={current.enabled}
                                    busy={setDefault.isPending}
                                    disabled={current.archived}
                                    showLabel
                                    onChange={
                                        canSetDefault
                                            ? (next) => {
                                                  setPendingDefaultChange({
                                                      field: 'enabled',
                                                      next,
                                                  });
                                              }
                                            : undefined
                                    }
                                    aria-label={`Platform default for ${definition.key}`}
                                />
                                <span className="text-[13px] text-gray-500">
                                    {current.rolloutPercentage === undefined
                                        ? 'No rollout gate — on means on for everyone.'
                                        : `Gated at ${String(current.rolloutPercentage)}% of users.`}
                                </span>
                                <span className="ml-auto text-[12px] text-gray-400">
                                    v{current.version} · {current.updatedBy}{' '}
                                    <span title={absoluteTime(current.updatedAt)}>
                                        {relativeTime(current.updatedAt)}
                                    </span>
                                </span>
                            </div>

                            <div className="flex items-center gap-3 border-t border-gray-100 pt-3.5">
                                <Toggle
                                    on={current.archived}
                                    busy={setDefault.isPending}
                                    // Archiving rides the same `PUT /default`
                                    // route, so it carries the same tier.
                                    onChange={
                                        canSetDefault
                                            ? (next) => {
                                                  setPendingDefaultChange({
                                                      field: 'archived',
                                                      next,
                                                  });
                                              }
                                            : undefined
                                    }
                                    aria-label={`Archived state for ${definition.key}`}
                                />
                                <span className="text-[13px] text-gray-500">
                                    <strong className="font-semibold text-gray-700">
                                        Archived
                                    </strong>{' '}
                                    — the real kill switch. Overrides an override.
                                </span>
                            </div>

                            <PropagationNotice />
                        </div>
                    </Card>

                    {/* ------------------------------------------- environment matrix - */}
                    <EnvironmentMatrix
                        flagKey={flagKey}
                        platformDefault={current}
                        environmentDefaults={environmentDefaults}
                    />

                    {/* -------------------------------------------- override rows - */}
                    <Card>
                        <CardHeader
                            title="Museums overriding this flag"
                            subtitle={`${String(overrides.length)} of them`}
                        />
                        {overrides.length === 0 ? (
                            <EmptyState
                                title="No museum overrides this flag"
                                description="Every museum resolves the platform default above."
                                action={
                                    <Button
                                        variant="ghost"
                                        disabled={!canOverride}
                                        onClick={() => {
                                            setOverriding(true);
                                        }}
                                    >
                                        <IconPlus className="size-4" />
                                        Override a museum
                                    </Button>
                                }
                            />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                                            <th className="px-5 py-3 font-semibold">Museum</th>
                                            <th className="px-5 py-3 font-semibold">
                                                Stored value
                                            </th>
                                            <th className="px-5 py-3 font-semibold">Resolves to</th>
                                            <th className="px-5 py-3 font-semibold">Rollout</th>
                                            <th className="px-5 py-3" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overrides.map((view) => (
                                            <OverrideRow
                                                key={view.override.tenantId}
                                                flagKey={flagKey}
                                                view={view}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </div>

                {/* ----------------------------------- definition + history sidecar - */}
                <div className="flex flex-col gap-4">
                    <Card>
                        <CardHeader title="Definition" />
                        <div className="px-5 py-2">
                            <KeyValue label="Name">{definition.name}</KeyValue>
                            <KeyValue label="Category">{definition.category}</KeyValue>
                            <KeyValue label="Lifecycle">
                                <Badge tone={LIFECYCLE_TONE[definition.lifecycle]}>
                                    {LIFECYCLE_LABEL[definition.lifecycle]}
                                </Badge>
                            </KeyValue>
                            <KeyValue label="Owner team">{definition.owner}</KeyValue>
                            <KeyValue label="Created by">{definition.createdBy}</KeyValue>
                            <KeyValue label="Created">
                                <span title={absoluteTime(definition.createdAt)}>
                                    {relativeTime(definition.createdAt)}
                                </span>
                            </KeyValue>
                        </div>
                    </Card>

                    <Card>
                        <CardHeader title="Change history" subtitle="Most recent first" />
                        <FlagHistory history={history} />
                    </Card>
                </div>
            </div>

            <OverrideDialog
                flagKey={flagKey}
                open={overriding}
                onClose={() => {
                    setOverriding(false);
                }}
            />

            <PlatformDefaultConfirmDialog
                flagKey={flagKey}
                pending={pendingDefaultChange}
                isPending={setDefault.isPending}
                error={setDefault.error}
                onCancel={() => {
                    setDefault.reset();
                    setPendingDefaultChange(null);
                }}
                onConfirm={() => {
                    if (!pendingDefaultChange) return;
                    setDefault.mutate(
                        defaultWriteFrom(current, {
                            [pendingDefaultChange.field]: pendingDefaultChange.next,
                        }),
                        { onSuccess: () => setPendingDefaultChange(null) },
                    );
                }}
            />
        </>
    );
}

/**
 * One overriding museum.
 *
 * Shows the **stored value** and the **resolved verdict** as separate columns
 * rather than one switch. They disagree more often than is comfortable: an
 * override of `true` on an archived flag resolves `false`, and so does one
 * behind a 0% rollout. A single toggle would have to pick one of the two to
 * display, and either choice tells an operator something untrue half the time.
 */
function OverrideRow({ flagKey, view }: { flagKey: string; view: FlagOverrideView }) {
    const { override, verdict } = view;
    const setOverride = useSetOverride(flagKey);
    const clearOverride = useClearOverride(flagKey);
    const busy = setOverride.isPending || clearOverride.isPending;
    const error = setOverride.error ?? clearOverride.error;
    // One tier for both controls. Reverting a museum to a default that is `on`
    // *enables* something for a customer, so it is not the gentler of the two.
    const canOverride = useCan('ops-csm');

    return (
        <>
            <tr className="border-t border-gray-100 text-[13.5px] hover:bg-gray-50">
                <td className="px-5 py-3">
                    <div className="font-mono text-xs text-gray-800">{override.tenantId}</div>
                </td>
                <td className="px-5 py-3">
                    <Toggle
                        on={override.enabled}
                        busy={busy}
                        showLabel
                        onChange={
                            canOverride
                                ? (next) => {
                                      setOverride.mutate({
                                          tenantId: override.tenantId,
                                          enabled: next,
                                          // Round-trip the rollout. Sending only
                                          // `enabled` would silently promote a
                                          // 25% gate to 100% for this museum,
                                          // and nothing downstream could tell
                                          // that apart from someone meaning it.
                                          ...(override.rolloutPercentage !== undefined
                                              ? { rolloutPercentage: override.rolloutPercentage }
                                              : {}),
                                      });
                                  }
                                : undefined
                        }
                        aria-label={`${flagKey} for ${override.tenantId}`}
                    />
                </td>
                <td className="px-5 py-3">
                    <VerdictBadge verdict={verdict} />
                </td>
                <td className="px-5 py-3 text-gray-700">
                    {override.rolloutPercentage === undefined ? (
                        <span className="text-xs text-gray-400">No gate</span>
                    ) : (
                        <span className="font-semibold">{override.rolloutPercentage}%</span>
                    )}
                </td>
                <td className="px-5 py-3 text-right">
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !canOverride}
                        onClick={() => {
                            clearOverride.mutate({ tenantId: override.tenantId });
                        }}
                    >
                        Revert to default
                    </Button>
                </td>
            </tr>
            {error && (
                <tr className="border-t border-gray-100">
                    <td colSpan={5} className="px-5 py-2.5">
                        <WriteError error={error} />
                    </td>
                </tr>
            )}
        </>
    );
}

/**
 * Why the flag resolves the way it does, not just whether it does.
 *
 * Each source carries its own preposition rather than sharing a "via " prefix —
 * "via the flag is archived" is not a sentence, and the two sources that are not
 * a *route* to a value (archived, unset) need different grammar from the three
 * that are.
 */
const VERDICT_REASON: Record<FlagVerdict['source'], string> = {
    override: 'via this override',
    default: 'via the platform default',
    rollout: 'via the rollout gate',
    archived: 'because the flag is archived',
    unset: 'no row exists',
};

function VerdictBadge({ verdict }: { verdict: FlagVerdict }) {
    return (
        <span className="inline-flex items-center gap-2">
            <Badge tone={verdict.enabled ? 'success' : 'neutral'} dot>
                {verdict.enabled ? 'On' : 'Off'}
            </Badge>
            <span className="text-xs text-gray-500">{VERDICT_REASON[verdict.source]}</span>
        </span>
    );
}

function KeyValue({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 py-2.5 text-[13px] last:border-0">
            <span className="shrink-0 text-gray-500">{label}</span>
            <span className="truncate text-right font-semibold text-gray-800">{children}</span>
        </div>
    );
}
